/* Copyright (c) 2020-present CloudIO Inc */

import { Slider, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Margin, MarginProp } from 'componentregistry';
import Icon from 'icons/Icon';
import React, { memo } from 'react';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';

import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
  },
  label: {
    flex: 0,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  option: {
    transition: 'color 0.2s',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    alignSelf: 'center',
    fontSize: '0.8rem',
    padding: '4px 0',
    position: 'relative',
    flex: 1,
    cursor: 'pointer',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '& > svg': {
      height: '1em',
      width: '1em',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {},
  slider: {
    flex: 2,
    height: '30px',
    overflow: 'visible',
    padding: '0 8px',
    zIndex: 1,
  },
  expander: {
    margin: '0 8px',
    outline: 'none',
    width: 14,
    '& > svg': {
      fill: theme.palette.text.secondary,
    },
    '&$selected': {
      '& > svg': {
        fill: theme.palette.primary.main,
      },
    },
    cursor: 'pointer',
  },
  right: {
    textAlign: 'right',
    paddingRight: 8,
  },
  input: {
    backgroundColor: theme.palette.background.paper,
    border: `4px solid ${theme.palette.background.default}`,
    color: theme.palette.text.primary,
    flexBasis: '60px',
    flexGrow: 0,
    flexShrink: 0,
    fontSize: '0.8rem',
    lineHeight: '22px',
    outline: 'none',
    padding: '0 8px',
    textAlign: 'center',
    width: '60px',
  },
}));

function MarginValueFn({
  label,
  value,
  onChange,
  expanded,
  property,
  isViewOnlyUser = false,
}: {
  label: string;
  value?: number;
  onChange: (val?: number, expand?: boolean) => void;
  expanded: boolean;
  property: string;
  isViewOnlyUser?: boolean;
}) {
  const classes = useStyles();
  const hasValue = value !== undefined;
  const disabled = label === 'all' ? expanded : !hasValue;

  function onClick() {
    if (label !== 'all') {
      onChange(value !== undefined ? undefined : 0);
    }
    if (label === 'all') {
      onChange(expanded ? undefined : value ?? 0, true);
    }
  }

  return (
    <div data-testid={`${property}-${label}`} className={classes.root}>
      <div
        className={clsx(classes.label, label !== 'all' ? classes.right : null)}
      >
        {label === 'all'
          ? 'Margin'
          : label.charAt(0).toUpperCase() + label.substring(1)}
      </div>
      <div className={classes.inner}>
        <div className={clsx(classes.option, classes.slider)}>
          <Slider
            disabled={disabled || isViewOnlyUser}
            valueLabelDisplay="auto"
            step={4}
            min={0}
            max={160}
            onChange={(_: Event, val: number | number[]) => {
              if (typeof val === 'number') {
                onChange(val);
              }
            }}
            value={value ?? 0}
          />
        </div>
        <input
          className={clsx(classes.option, classes.input)}
          disabled={disabled || isViewOnlyUser}
          value={value ?? ''}
          onChange={(e) => {
            onChange(e.target.value ? parseInt(e.target.value, 10) : 0);
          }}
        />
        <div
          aria-disabled={isViewOnlyUser}
          onKeyDown={(e) => clickOnEnterOrSpace(e, onClick)}
          role="button"
          tabIndex={-1}
          data-tip={
            // eslint-disable-next-line no-nested-ternary
            label !== 'all'
              ? hasValue
                ? `Clear ${label} margin`
                : `Set ${label} margin`
              : expanded
              ? 'Remove independent margin'
              : 'Set independent margin'
          }
          className={clsx(
            classes.expander,
            label !== 'all' && hasValue ? classes.selected : null,
            label === 'all' && expanded ? classes.selected : null,
          )}
          onClick={onClick}
        >
          {label === 'all' ? (
            /* <svg viewBox="0 0 448 512">
              <path d="M0 180V56c0-13.3 10.7-24 24-24h124c6.6 0 12 5.4 12 12v8c0 6.6-5.4 12-12 12H32v116c0 6.6-5.4 12-12 12h-8c-6.6 0-12-5.4-12-12zM300 32h124c13.3 0 24 10.7 24 24v124c0 6.6-5.4 12-12 12h-8c-6.6 0-12-5.4-12-12V64H300c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12zm148 300v124c0 13.3-10.7 24-24 24H300c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h116V332c0-6.6 5.4-12 12-12h8c6.6 0 12 5.4 12 12zM148 480H24c-13.3 0-24-10.7-24-24V332c0-6.6 5.4-12 12-12h8c6.6 0 12 5.4 12 12v116h116c6.6 0 12 5.4 12 12v8c0 6.6-5.4 12-12 12z" />
            </svg>  */
            <Icon iconName={expanded ? 'compress' : 'expand'} />
          ) : null}
          {label !== 'all' && !hasValue ? (
            <svg
              style={{ pointerEvents: 'none' }}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm16 400c0 8.8-7.2 16-16 16H48c-8.8 0-16-7.2-16-16V80c0-8.8 7.2-16 16-16h352c8.8 0 16 7.2 16 16v352z" />
            </svg>
          ) : null}
          {label !== 'all' && hasValue ? (
            <svg
              style={{ pointerEvents: 'none' }}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path d="M400 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zm0 32c8.823 0 16 7.178 16 16v352c0 8.822-7.177 16-16 16H48c-8.822 0-16-7.178-16-16V80c0-8.822 7.178-16 16-16h352m-34.301 98.293l-8.451-8.52c-4.667-4.705-12.265-4.736-16.97-.068l-163.441 162.13-68.976-69.533c-4.667-4.705-12.265-4.736-16.97-.068l-8.52 8.451c-4.705 4.667-4.736 12.265-.068 16.97l85.878 86.572c4.667 4.705 12.265 4.736 16.97.068l180.48-179.032c4.704-4.667 4.735-12.265.068-16.97z" />
            </svg>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MarginProperty({
  activeItemId,
  pageId,
  property,
  mgr,
  value = getDefaultValues<Margin>(mgr, property),
  onChange,
  isViewOnlyUser = false,
}: PropertyEditorProps<MarginProp, Margin>) {
  const expanded = !!(
    value.top !== undefined ||
    value.right !== undefined ||
    value.bottom !== undefined ||
    value.left !== undefined
  );
  const sides = ['all', 'top', 'right', 'bottom', 'left'] as const;

  return (
    <>
      {expanded ? (
        sides.map((side: keyof Margin) => (
          <MarginValueFn
            isViewOnlyUser={isViewOnlyUser}
            property={property}
            expanded={expanded}
            key={side}
            label={side}
            value={value[side]}
            onChange={(m, expand) => {
              if (expand) {
                onChange(property, undefined);
              } else if (value && m === undefined) {
                const val = { ...value };
                delete val[side];
                if (Object.keys(val).length) {
                  onChange(property, val);
                } else {
                  onChange(property, undefined);
                }
              } else {
                onChange(property, { ...value, [side]: m });
              }
            }}
          />
        ))
      ) : (
        <MarginValueFn
          isViewOnlyUser={isViewOnlyUser}
          property={property}
          expanded={false}
          key="all"
          label="all"
          value={value.all}
          onChange={(v, expand) => {
            onChange(
              property,
              expand
                ? {
                    top: v,
                    bottom: v,
                    right: v,
                    left: v,
                  }
                : { all: v },
            );
          }}
        />
      )}
    </>
  );
}

export default memo(MarginProperty);
